import React, { useState, useEffect } from 'react';
import { Star, Smile } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    InlineInput, AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';

const DUMMY_DATA = {
    question: "How would you rate today's speaker? ⭐",
    settings: { ratingType: 'stars', maxValue: 5, lowLabel: 'Poor', highLabel: 'Excellent', description: true, descText: 'Tap the number of stars that best reflects your experience.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const Rating = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState({
        ratingType: 'stars',
        maxValue: 5,
        lowLabel: '',
        highLabel: '',
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                {/* Stars / Emojis */}
                <SettingsRow label="Rating type">
                    <div className={styles.ratingTypeBtns}>
                        {[
                            { val: 'stars', label: 'Stars', Icon: Star },
                            { val: 'emojis', label: 'Emojis', Icon: Smile },
                        ].map((opt) => (
                            <button
                                key={opt.val}
                                type="button"
                                onClick={() => set('ratingType', opt.val)}
                                className={`${styles.ratingTypeBtn} ${settings.ratingType === opt.val ? styles.ratingTypeBtnActive : ''}`}
                            >
                                <opt.Icon size={13} style={{ marginRight: 4, verticalAlign: -2 }} aria-hidden="true" />
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </SettingsRow>

                {/* Maximum Value */}
                <SettingsRow label="Maximum value" description="Choose on which scale participants can rate">
                    <InlineInput
                        type="number"
                        value={settings.maxValue}
                        onChange={(e) => set('maxValue', e.target.value)}
                        min={2}
                        max={10}
                    />
                </SettingsRow>

                {/* Low score label */}
                <SettingsRow label="Low score label">
                    <InlineInput
                        value={settings.lowLabel}
                        onChange={(e) => set('lowLabel', e.target.value)}
                        placeholder='e.g. "Not likely"'
                    />
                </SettingsRow>

                {/* High score label */}
                <SettingsRow label="High score label">
                    <InlineInput
                        value={settings.highLabel}
                        onChange={(e) => set('highLabel', e.target.value)}
                        placeholder='e.g. "Very likely"'
                    />
                </SettingsRow>

                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
            {settings.imageUrl && (
                <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
            )}
            <div className={styles.questionContent}>
                <div className={styles.questionLabelRow}>
                    <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                    <span className={styles.aiActions}>
                        <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                        {previousQuestion !== null && (
                            <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                        )}
                    </span>
                </div>
                {editingQuestion ? (
                    <input
                        className={styles.questionInput}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="How would you rate this session?"
                        autoFocus
                        onBlur={closeQuestionEditor}
                        onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                    />
                ) : (
                    <Tooltip title="Click to edit question" placement="top">
                        <div
                            className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={openQuestionEditor}
                            onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                        >
                            {question || 'Type your question here...'}
                        </div>
                    </Tooltip>
                )}

                {settings.description && (
                    <div className={styles.questionDescription}>
                        <div className={styles.questionLabelRow}>
                            <span className={styles.descLabel}>Description</span>
                            <span className={styles.aiActions}>
                                <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                {previousDescText !== null && (
                                    <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                )}
                            </span>
                        </div>
                        {editingDesc ? (
                            <input
                                className={styles.descInput}
                                value={settings.descText}
                                onChange={(e) => set('descText', e.target.value)}
                                placeholder="Type description here..."
                                autoFocus
                                onBlur={closeDescEditor}
                                onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                            />
                        ) : (
                            <Tooltip title="Click to edit description" placement="top">
                                <div
                                    className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openDescEditor}
                                    onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                >
                                    {settings.descText || 'Type description here...'}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* Scale Preview — matches participant: maxValue 2–10, stars or emojis */}
                {(() => {
                    const maxVal = Math.min(Math.max(2, Number(settings.maxValue) || 5), 10);
                    const E5 = ['😠', '😕', '😐', '🙂', '😄'];
                    const E10 = ['😠', '😕', '😫', '😐', '🙂', '😊', '😄', '😁', '🤩', '💯'];
                    const previewSymbols = settings.ratingType === 'stars'
                        ? Array.from({ length: maxVal }, () => '⭐')
                        : (maxVal <= 5 ? E5 : E10).slice(0, maxVal);
                    const isStar = settings.ratingType === 'stars';
                    return (
                        <div className={styles.ratingPreview}>
                            <span className={styles.ratingPreviewLabel}>
                                {settings.lowLabel || 'Low'}
                            </span>
                            {previewSymbols.map((sym, i) => (
                                <span key={i} className={isStar ? styles.ratingSymbolStar : styles.ratingSymbol}>
                                    {sym}
                                </span>
                            ))}
                            <span className={styles.ratingPreviewLabel}>
                                {settings.highLabel || 'High'}
                            </span>
                        </div>
                    );
                })()}
            </div>
        </div>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'rating',
                title: question || 'Rating',
                content: { question, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<Star size={20} />}
            color="var(--cat-amber)"
            title="Rating"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Rating;
